/**
 * Durable policy-inbox scan status for human-facing coverage surfaces.
 *
 * The external, user-authorized AI reads the source. CongressMCP only keeps
 * the bounded run and selected-message receipt bookkeeping exposed by this
 * endpoint. Keep this hook deliberately free of inbox evidence fields so it
 * remains safe to reuse in the dashboard and global chrome.
 */
import { useQuery } from "@tanstack/react-query";

export type PolicyInboxScanState = "running" | "completed" | "partial" | "failed" | "abandoned";

export interface PolicyInboxScanReceiptSummary {
  selected_message_count: number;
  persisted_message_count: number;
  failed_message_count: number;
  retry_attempt_count: number;
  by_disposition: {
    imported: number;
    duplicate: number;
    reconciled: number;
  };
  safe_errors?: Record<string, number>;
}

export interface PolicyInboxScan {
  id: string;
  source_key: string;
  source_kind: "email" | "browser" | "export" | "paste" | "other";
  imported_via: string;
  status: PolicyInboxScanState;
  requested_from: string;
  requested_through: string;
  covered_through: string | null;
  messages_examined: number;
  messages_selected: number;
  messages_persisted: number;
  messages_duplicate: number;
  messages_reconciled: number;
  messages_failed: number;
  started_at: string;
  last_activity_at: string;
  completed_at: string | null;
  receipt_summary: PolicyInboxScanReceiptSummary;
}

export interface PolicyInboxSourceCoverage {
  source_key: string;
  source_kind: PolicyInboxScan["source_kind"];
  latest_run: PolicyInboxScan;
  last_successfully_covered_through: string | null;
}

export interface PolicyInboxScansData {
  scans: PolicyInboxScan[];
  sources: PolicyInboxSourceCoverage[];
  active_scan_count: number;
}

const EMPTY_RECEIPTS: PolicyInboxScanReceiptSummary = {
  selected_message_count: 0,
  persisted_message_count: 0,
  failed_message_count: 0,
  retry_attempt_count: 0,
  by_disposition: { imported: 0, duplicate: 0, reconciled: 0 },
  safe_errors: {},
};

function finiteCount(value: unknown, fallback = 0): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? Math.trunc(parsed) : fallback;
}

function nullableString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function normalizeScan(value: unknown): PolicyInboxScan | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  const id = nullableString(row.id);
  const sourceKey = nullableString(row.source_key);
  const sourceKind = nullableString(row.source_kind);
  const status = nullableString(row.status);
  const requestedFrom = nullableString(row.requested_from);
  const requestedThrough = nullableString(row.requested_through);
  const startedAt = nullableString(row.started_at);
  const lastActivityAt = nullableString(row.last_activity_at);
  if (
    !id || !sourceKey || !requestedFrom || !requestedThrough || !startedAt || !lastActivityAt
    || !["email", "browser", "export", "paste", "other"].includes(sourceKind ?? "")
    || !["running", "completed", "partial", "failed", "abandoned"].includes(status ?? "")
  ) return null;

  const rawReceipt = row.receipt_summary && typeof row.receipt_summary === "object" && !Array.isArray(row.receipt_summary)
    ? row.receipt_summary as Record<string, unknown>
    : {};
  const rawDisposition = rawReceipt.by_disposition && typeof rawReceipt.by_disposition === "object" && !Array.isArray(rawReceipt.by_disposition)
    ? rawReceipt.by_disposition as Record<string, unknown>
    : {};
  const messagesSelected = finiteCount(row.messages_selected);
  const messagesPersisted = finiteCount(row.messages_persisted);
  const messagesFailed = finiteCount(row.messages_failed);
  const messagesDuplicate = finiteCount(row.messages_duplicate);
  const messagesReconciled = finiteCount(row.messages_reconciled);

  return {
    id,
    source_key: sourceKey,
    source_kind: sourceKind as PolicyInboxScan["source_kind"],
    imported_via: nullableString(row.imported_via) ?? "other_mcp",
    status: status as PolicyInboxScanState,
    requested_from: requestedFrom,
    requested_through: requestedThrough,
    covered_through: nullableString(row.covered_through),
    messages_examined: finiteCount(row.messages_examined),
    messages_selected: messagesSelected,
    messages_persisted: messagesPersisted,
    messages_duplicate: messagesDuplicate,
    messages_reconciled: messagesReconciled,
    messages_failed: messagesFailed,
    started_at: startedAt,
    last_activity_at: lastActivityAt,
    completed_at: nullableString(row.completed_at),
    receipt_summary: {
      selected_message_count: finiteCount(rawReceipt.selected_message_count, messagesSelected),
      persisted_message_count: finiteCount(rawReceipt.persisted_message_count, messagesPersisted),
      failed_message_count: finiteCount(rawReceipt.failed_message_count, messagesFailed),
      retry_attempt_count: finiteCount(rawReceipt.retry_attempt_count),
      by_disposition: {
        imported: finiteCount(rawDisposition.imported, Math.max(0, messagesPersisted - messagesDuplicate - messagesReconciled)),
        duplicate: finiteCount(rawDisposition.duplicate, messagesDuplicate),
        reconciled: finiteCount(rawDisposition.reconciled, messagesReconciled),
      },
      safe_errors: rawReceipt.safe_errors && typeof rawReceipt.safe_errors === "object" && !Array.isArray(rawReceipt.safe_errors)
        ? Object.fromEntries(
            Object.entries(rawReceipt.safe_errors as Record<string, unknown>)
              .map(([key, count]) => [key, finiteCount(count)]),
          )
        : EMPTY_RECEIPTS.safe_errors,
    },
  };
}

function deriveSources(scans: PolicyInboxScan[]): PolicyInboxSourceCoverage[] {
  const sourceOrder: string[] = [];
  const grouped = new Map<string, PolicyInboxScan[]>();
  for (const scan of scans) {
    const key = `${scan.source_kind}\u0000${scan.source_key}`;
    if (!grouped.has(key)) sourceOrder.push(key);
    grouped.set(key, [...(grouped.get(key) ?? []), scan]);
  }
  return sourceOrder.map((key) => {
    const sourceScans = grouped.get(key)!;
    const latestRun = sourceScans[0];
    const safelyCovered = sourceScans
      .filter((scan) => (
        scan.status === "completed"
        || (["partial", "failed", "abandoned"].includes(scan.status) && scan.messages_failed === 0)
      ) && scan.covered_through)
      .sort((a, b) => String(b.covered_through).localeCompare(String(a.covered_through)))[0];
    return {
      source_key: latestRun.source_key,
      source_kind: latestRun.source_kind,
      latest_run: latestRun,
      last_successfully_covered_through: safelyCovered?.covered_through ?? null,
    };
  });
}

function normalizeResponse(value: unknown): PolicyInboxScansData {
  const payload = value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
  const scans = (Array.isArray(payload.scans) ? payload.scans : [])
    .map(normalizeScan)
    .filter((scan): scan is PolicyInboxScan => scan !== null)
    .sort((a, b) => Date.parse(b.started_at) - Date.parse(a.started_at));
  return {
    scans,
    // Derive this client-side from the canonical run list. That keeps the UI
    // compatible with both the minimal endpoint and richer future rollups.
    sources: deriveSources(scans),
    active_scan_count: scans.filter((scan) => scan.status === "running").length,
  };
}

export function humanizePolicyInboxSource(sourceKey: string): string {
  return sourceKey
    .split(/[._:-]+/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ") || "Authorized source";
}

export function policyInboxMediatorLabel(value: string): string {
  if (value === "claude") return "Claude";
  if (value === "cursor") return "Cursor";
  if (value === "chatgpt") return "ChatGPT";
  if (value === "web_ui") return "CongressMCP AI";
  if (value === "api_direct") return "API client";
  return "Connected AI";
}

export function usePolicyInboxScans(workspaceId: string | null | undefined, limit = 20) {
  return useQuery<PolicyInboxScansData>({
    queryKey: ["policy-inbox-scans", workspaceId, limit],
    queryFn: async () => {
      const res = await fetch(
        `/api/v1/workspaces/${encodeURIComponent(workspaceId!)}/policy-inbox-scans?limit=${limit}`,
        { credentials: "include", cache: "no-store" },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || body.message || `Failed to load source coverage (${res.status})`);
      }
      return normalizeResponse(await res.json());
    },
    enabled: !!workspaceId,
    staleTime: 10_000,
    refetchInterval: (query) => query.state.data?.active_scan_count ? 10_000 : false,
  });
}
